/**
 * External FHIR system URLs (canonical source of truth).
 *
 * These URLs identify code systems, value sets, identifier systems, and
 * extensions defined by external Standards Development Organizations:
 * HL7 (FHIR R4 base + terminology), IETF (BCP-47), SNOMED, LOINC, NUCC,
 * NIH/RxNorm, US Core IG, HL7 SDC IG, CMS, IHE, and a small set of
 * project-external healthcare URIs (e.g. Stedi).
 *
 * Alopex-owned canonical URLs (`http://alopexcare.com/...`) are NOT here;
 * they are generated automatically from the IG resources into
 * `../generated/canonical-urls.ts` by `npm run generate:terminologies`
 * (see SF2DEV-275 Decision #7).
 *
 * To add a new external URL: add it to the appropriate grouping below.
 * Then re-export from `apps/web/src/features/shared/constants/fhir-systems.ts`
 * or `apps/medplum/bots/shared/fhir-constants.ts` if backwards-compat
 * naming is needed for the existing consumer surface.
 */
/**
 * HL7 standard CodeSystems (FHIR R4 base + `terminology.hl7.org`).
 * Reference: https://terminology.hl7.org/
 */
export const HL7_CODE_SYSTEMS = {
    /** Identifier types (MR, SS, DL, etc.) — v2 table 0203 */
    IDENTIFIER_TYPE: 'http://terminology.hl7.org/CodeSystem/v2-0203',
    /** Marital status codes (M, S, D, W, T, A, UNK, etc.) — HL7 v3 */
    MARITAL_STATUS: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
    /** Administrative gender (male, female, other, unknown) — FHIR R4 base */
    ADMINISTRATIVE_GENDER: 'http://hl7.org/fhir/administrative-gender',
    /** Contact relationship (emergency, family, etc.) — v2 table 0131 */
    CONTACT_RELATIONSHIP: 'http://terminology.hl7.org/CodeSystem/v2-0131',
    /** Address use (home, work, temp, old) — FHIR R4 base */
    ADDRESS_USE: 'http://hl7.org/fhir/address-use',
    /** Contact point use (home, work, mobile) — FHIR R4 base */
    CONTACT_POINT_USE: 'http://hl7.org/fhir/contact-point-use',
    /** EpisodeOfCare.status (planned, waitlist, active, onhold, finished, cancelled, entered-in-error) — FHIR R4 base */
    EPISODE_OF_CARE_STATUS: 'http://hl7.org/fhir/episode-of-care-status',
};
/**
 * HL7 v3 / terminology-server CodeSystems beyond the common HL7_CODE_SYSTEMS set.
 */
export const HL7_TERMINOLOGY = {
    /** v3-ParticipationMode (PHONE, EMAILWRIT, FAXWRIT, FACE) for Communication.medium */
    V3_PARTICIPATION_MODE: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
    /** v3-ActCode — generic act-classification codes */
    V3_ACT_CODE: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
    /** v3-ParticipationType — generic participation-type codes */
    V3_PARTICIPATION_TYPE: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
    /** v3-RoleCode — broad role taxonomy (used by HL7 facility-type subset) */
    V3_ROLE_CODE: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
    /** v3-ReligiousAffiliation — HL7-defined religion codes */
    V3_RELIGIOUS_AFFILIATION: 'http://terminology.hl7.org/CodeSystem/v3-ReligiousAffiliation',
    /** v3-DataOperation — Provenance.activity */
    V3_DATA_OPERATION: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation',
    /** provenance-participant-type — Provenance.agent.type */
    V3_PROVENANCE_PARTICIPANT_TYPE: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type',
    /** v3-ObservationInterpretation — Observation.interpretation */
    V3_OBSERVATION_INTERPRETATION: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
    /** consentscope — Consent.scope */
    CONSENT_SCOPE: 'http://terminology.hl7.org/CodeSystem/consentscope',
    /** observation-category — Observation.category */
    OBSERVATION_CATEGORY: 'http://terminology.hl7.org/CodeSystem/observation-category',
    /** condition-category — Condition.category (problem-list-item, health-concern, encounter-diagnosis) */
    CONDITION_CATEGORY: 'http://terminology.hl7.org/CodeSystem/condition-category',
    /** organization-type — Organization.type */
    ORGANIZATION_TYPE: 'http://terminology.hl7.org/CodeSystem/organization-type',
    /** endpoint-payload-type — Endpoint.payloadType (incl. "none" for a UI-only endpoint) */
    ENDPOINT_PAYLOAD_TYPE: 'http://terminology.hl7.org/CodeSystem/endpoint-payload-type',
};
/**
 * FHIR R4 base StructureDefinitions for extensions widely reused across IGs.
 */
export const FHIR_R4_EXTENSIONS = {
    /** Patient.extension:religion — FHIR R4 core patient-religion extension */
    PATIENT_RELIGION: 'http://hl7.org/fhir/StructureDefinition/patient-religion',
    /** ordinalValue extension — assigns an ordinal score to a value */
    ORDINAL_VALUE: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
};
/**
 * Standard FHIR identifier system URIs (patient / practitioner / organization).
 */
export const FHIR_IDENTIFIER_SYSTEMS = {
    /** US National Provider Identifier */
    US_NPI: 'http://hl7.org/fhir/sid/us-npi',
    /** NUCC Healthcare Provider Taxonomy */
    NUCC_TAXONOMY: 'http://nucc.org/provider-taxonomy',
    /** US Driver's License */
    US_DRIVERS_LICENSE: 'http://hl7.org/fhir/sid/us-dl',
    /** ICD-10-CM (Clinical Modification, US specific) */
    ICD_10: 'http://hl7.org/fhir/sid/icd-10-cm',
    /** AMA CPT (incl. CPT Category II quality codes) */
    CPT: 'http://www.ama-assn.org/go/cpt',
    /** SNOMED CT clinical terminology */
    SNOMED_CT: 'http://snomed.info/sct',
    /** LOINC (Logical Observation Identifiers Names and Codes) */
    LOINC: 'http://loinc.org',
    /** RxNorm (medication coding) */
    RXNORM: 'http://www.nlm.nih.gov/research/umls/rxnorm',
    /** IETF BCP-47 language tags */
    IETF_BCP_47: 'urn:ietf:bcp:47',
};
/**
 * US Core IG extensions (StructureDefinitions defined by hl7.fhir.us.core 6.1.0).
 */
export const US_CORE_EXTENSIONS = {
    /** US Core Race */
    RACE: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',
    /** US Core Ethnicity */
    ETHNICITY: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',
    /** US Core Birth Sex */
    BIRTH_SEX: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
    /** US Core Tribal Affiliation */
    TRIBAL_AFFILIATION: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-tribal-affiliation',
    /** US Core Individual Pronouns */
    INDIVIDUAL_PRONOUNS: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-individual-pronouns',
};
/**
 * US Core IG CodeSystems.
 */
export const US_CORE_CODE_SYSTEMS = {
    /** US Core DocumentReference.category (clinical-note fixed slice) */
    DOC_REFERENCE_CATEGORY: 'http://hl7.org/fhir/us/core/CodeSystem/us-core-documentreference-category',
};
/**
 * HL7 Structured Data Capture IG (SDC) extensions used by Questionnaire processing.
 */
export const HL7_SDC_EXTENSIONS = {
    /** Pre-computed expression for a Questionnaire item */
    CALCULATED_EXPRESSION: 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-calculatedExpression',
    /** Observation extraction from a Questionnaire item */
    OBSERVATION_EXTRACT: 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-observationExtract',
};
/**
 * External CodeSystem URLs sourced from non-HL7 SDOs.
 */
export const EXTERNAL_CODE_SYSTEMS = {
    /** CMS Place of Service codes (Location.type[placeOfService] source) */
    CMS_PLACE_OF_SERVICE: 'https://www.cms.gov/Medicare/Coding/place-of-service-codes',
    /** Stedi payer directory network */
    STEDI_NETWORK: 'https://www.stedi.com/healthcare/network',
};
